import { useCallback, useEffect, useRef, useState } from "react";
import { getProperties } from "../api";
import { formatPropertyLabel } from "../utils/propertyLabel";

// One page of search results, matching the header property dropdown.
export const PAGE_SIZE = 20;

// Page size for the full-catalog sweep behind "All properties". A single
// `limit=totalCount` request is silently capped server-side, which would drop
// the tail of the catalog and assign only a partial set; paging avoids that.
export const CATALOG_PAGE_SIZE = 500;

// The package's contract is {propertyId, propertyName}; the CRS code is baked
// into the display name so multi-instance hotels (offline vs CRS) are
// distinguishable in the "Properties assigned" picker — matching the header
// property dropdown.
const toProperty = (property) => ({
  propertyId: property.propertyId,
  propertyName: formatPropertyLabel(property),
});

const fetchPage = ({ searchText = "", page = 1, limit = PAGE_SIZE } = {}) =>
  getProperties({ searchText, page, limit }).then(
    (res) => res?.data?.data ?? {}
  );

/**
 * Collects the caller's entire property catalog. `BE/api/v1/properties` is scoped to the logged-in
 * user, so this is exactly the set of properties this admin may assign *and* unassign — it backs
 * both the "All properties" tick and the `selected` scope check.
 *
 * The endpoint has no "give me everything" mode and caps the page size server-side (MAX_LIMIT=200),
 * so a single `limit=totalCount` request silently returns only the first slice. We page through
 * until the catalog is collected, using the size the backend actually returned (which may be below
 * CATALOG_PAGE_SIZE if it capped us) so no rows are skipped between pages. Callers must cache the
 * result; it is the expensive path.
 */
const fetchEveryProperty = async () => {
  const first = await fetchPage({ page: 1, limit: CATALOG_PAGE_SIZE });
  const totalCount = first.totalCount ?? 0;
  const firstBatch = first.properties ?? [];
  const rows = firstBatch.map(toProperty);

  // The backend may cap `limit` below what we asked; page by the size it
  // actually honored so page 2 starts exactly where page 1 ended.
  const pageSize = firstBatch.length;
  if (totalCount === 0 || pageSize === 0) {
    return rows;
  }

  const totalPages = Math.ceil(totalCount / pageSize);
  for (let page = 2; page <= totalPages; page += 1) {
    // eslint-disable-next-line no-await-in-loop -- pages must be sequential; the API is page-numbered.
    const data = await fetchPage({ page, limit: pageSize });
    const batch = data.properties ?? [];
    if (batch.length === 0) break; // Guard against a short/empty tail page.
    rows.push(...batch.map(toProperty));
  }

  return rows;
};

/**
 * `useProperties` for `@hoteltradergit/user-management`, in the package's server-search mode.
 *
 * Replaces `usePropertyStore`, which only ever held the first 20 properties — so the user form's
 * "Properties assigned" picker could search no further than page 1. Here the typed text (already
 * debounced by the package) goes to the API, exactly like the header property dropdown.
 *
 * `selected` returns the caller's WHOLE catalog rather than just the rows for `query.ids`, and
 * `allManageable` is deliberately not set. That combination is what makes scoped remove icons and a
 * working "All properties" deselect co-exist.
 *
 * The package keeps one cumulative id -> label map, fed only from `properties` and `selected`, and
 * derives manageability from it: `He = allManageable || labelMap[id]`. Two behaviours hang off that
 * single map:
 *
 *  - Remove icons. An assigned id absent from the map renders as a locked chip. Handing over the
 *    whole catalog marks exactly the in-scope ids as manageable; an assignment made by a
 *    wider-scoped admin stays locked, and still shows a readable label from the user API
 *    (`userPropertyMap`, the package's second fallback).
 *  - Deselect-all. It keeps every selected id it deems unmanageable (`q.filter(e => !He(e))`).
 *    `getAllPropertyIds` returns bare ids and `query.ids` is never updated from a select-all, so
 *    with only the search page in the map, un-ticking cleared just those 20 and left the rest
 *    selected. With the catalog in the map, un-ticking clears the whole portfolio.
 *
 * Cost: resolving scope needs the catalog, and the properties API has no `ids` filter, so an edit
 * page must sweep — one request per 200 rows, 23+ on a ~4.5k-property tenant. It runs in the
 * background: `loading` covers only the page-of-20 search, so the form is usable immediately and
 * icons settle when the sweep lands. Adding a user needs no sweep at all, and a select-all reuses
 * the same cached one. Revisit if the properties API grows an `ids` filter — scope would then cost
 * a single request and the sweep could go.
 */
const useUmProperties = (query) => {
  const search = query?.search ?? "";
  const hasAssignedIds = (query?.ids ?? []).length > 0;

  const [page, setPage] = useState({
    properties: [],
    totalCount: 0,
    loading: true,
  });

  // The caller's whole catalog, once swept. Handed to the package as `selected`, which is the only
  // channel it offers for teaching its manageability map about ids beyond the current search page.
  // Empty until the sweep lands, which is the fail-closed state: nothing removable, nothing wrongly
  // cleared. Stored as state (not a ref) so the package's effect re-runs when it arrives, and
  // replaced only once so the array identity stays stable across renders.
  const [catalog, setCatalog] = useState([]);

  // Only the latest search may write to state; slow responses for stale keywords are dropped.
  const requestIdRef = useRef(0);
  const catalogRef = useRef(null);

  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setPage((prev) => ({ ...prev, loading: true }));

    fetchPage({ searchText: search })
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setPage({
          properties: (data.properties ?? []).map(toProperty),
          totalCount: data.totalCount ?? 0,
          loading: false,
        });
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setPage({ properties: [], totalCount: 0, loading: false });
      });
  }, [search]);

  // Cache the sweep so the scope resolve, "All properties" and any re-tick share one fetch; a
  // rejection is not cached, so a later attempt retries. Publishing to `catalog` here means the
  // rows reach the package whichever path paid for them — a select-all on the add screen feeds the
  // map just as an edit-page mount does.
  const getEveryProperty = useCallback(() => {
    if (!catalogRef.current) {
      catalogRef.current = fetchEveryProperty()
        .then((rows) => {
          setCatalog(rows);
          return rows;
        })
        .catch((error) => {
          catalogRef.current = null;
          throw error;
        });
    }
    return catalogRef.current;
  }, []);

  const getAllPropertyIds = useCallback(
    () => getEveryProperty().then((rows) => rows.map((p) => p.propertyId)),
    [getEveryProperty]
  );

  // Edit pages need the catalog up front to decide which assigned chips are removable. Deliberately
  // not folded into `loading` — see the block comment: it must not hold up the form.
  useEffect(() => {
    if (!hasAssignedIds) return;
    // Swallowed: a failed sweep leaves `catalog` empty, which locks every chip. That loses the
    // ability to unassign but never grants removal of an out-of-scope property.
    getEveryProperty().catch(() => {});
  }, [hasAssignedIds, getEveryProperty]);

  return {
    properties: page.properties,
    loading: page.loading,
    serverSearch: true,
    totalCount: page.totalCount,
    // The whole catalog, not just `query.ids`: the package merges this into the map that drives
    // both remove icons and deselect-all. See the block comment above.
    selected: catalog,
    getAllPropertyIds,
  };
};

export default useUmProperties;
